import { extensionsBanner } from "#web/assets";
import { BlockedCodesTable } from "#web/components/blocking/BlockedCodesTable";
import ExtensionAttributionList from "#web/components/blocking/ExtensionAttributionList";
import FAQ from "#web/components/blocking/FAQ";
import { ProtectedOrders } from "#web/components/blocking/ProtectedOrders";
import { RecoveredRevenueWithCommissionMetricCard } from "#web/components/blocking/RecoveredRevenueWithComissionMetricCard";
import { MetricCard } from "#web/components/common/MetricCard";
import { DateRangePicker } from "#web/components/DateRangePicker";
import { ExtensionsSupported } from "#web/components/settings/ExtensionsSupported";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { VerifyCheckoutExtensionInstalledQuery } from "#web/components/shop/queries/verifyCheckoutExtensionInstalledQuery";
import { routeConfig } from "#web/configs/route";
import { i18nLike } from "#web/services/i18nLike";
import { api } from "#web/services/api";
import { fetchTinybird, parsePipeResult } from "#web/services/tinybird";
import { toyyyyMMdd } from "#web/utils/dateFormatter";
import { getCachedDateOfNow } from "#web/utils/getCachedDateOfNow";
import { useAction } from "@gadgetinc/react";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  InlineGrid,
  Layout,
  Link,
  Modal,
  Page,
  TabProps,
  Tabs,
} from "@shopify/polaris";
import { ExportIcon, SettingsIcon } from "@shopify/polaris-icons";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as v from "valibot";
import { CardSkeleton } from "#web/components/common/CardSkeleton";

const en = {
  backAction: "Dashboard",
  error: {
    description: "Please try again later. If the issue persists, please contact support.",
    title: "Error loading page",
  },
  mode: {
    blockmax: "Maximum Blocking",
    blockonce: "Basic Blocking",
    blocksession: "Strong Blocking",
    disabled: "Disabled",
    tracking: "Tracking only",
    unknown: "Unknown mode",
  },
  protectedExtensions: "Protected Extensions",
  export: "Export",
  settings: "Settings",
  subtitle: "Block coupon extensions and protect your margins. Get insights on blocked codes and extension usage.",
  tabs: {
    blockedCodes: "Blocked Codes",
    overview: "Overview",
    protectedOrders: "Protected Orders",
  },
  title: "Extension Blocking",
};

const BlockingMetricsQuery = ({
  endDate,
  shopId,
  startDate,
}: {
  endDate: string;
  shopId: string | undefined;
  startDate: string;
}) =>
  queryOptions({
    enabled: !!shopId,
    queryFn: () => {
      const BlockingMetricsData = v.array(
        v.object({
          blocked_codes: v.nullish(v.number()),
          blocked_codes_tracked: v.nullish(v.number()),
          order_count: v.nullish(v.number()),
          orders_protected: v.nullish(v.number()),
          recovered_per_order: v.nullish(v.number()),
          recovered_revenue: v.nullish(v.number()),
          recovered_revenue_commission: v.nullish(v.number()),
          sessions_protected: v.nullish(v.number()),
          unique_blocked_codes: v.nullish(v.number()),
          unique_blocked_codes_tracked: v.nullish(v.number()),
          visitor_count: v.nullish(v.number()),
        }),
      );
      return fetchTinybird("blocking_metrics", { endDate, shopId: shopId!, startDate }).then(
        parsePipeResult(BlockingMetricsData),
      );
    },
    queryKey: ["blocking_metrics", shopId, startDate, endDate],
  });

export const Extensions = () => {
  const t = i18nLike(en);

  const mapMode = (mode: string) => {
    switch (mode) {
      case "blockmax":
        return t("mode.blockmax");
      case "blockonce":
        return t("mode.blockonce");
      case "blocksession":
        return t("mode.blocksession");
      case "disabled":
        return t("mode.disabled");
      case "tracking":
        return t("mode.tracking");
      default:
        return t("mode.unknown");
    }
  };

  const { data: shopData, isError: shopError } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;

  const [{ data: reportData, fetching: reportFetching, error: reportError }, generateReport] = useAction(
    api.shopifyShop.generateTrackingReport,
  );

  const downloadTrackingReport = () => {
    if (!shop?.id) return;

    generateReport({
      id: shop.id,
      startDate: pickerDate.start,
      endDate: pickerDate.end,
    });
  };

  // Auto-download when report data is available
  useEffect(() => {
    if (reportData) {
      // Create blob and download
      const blob = new Blob([reportData], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `tracking-report-${toyyyyMMdd(getCachedDateOfNow())}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }, [reportData]);

  const navigate = useNavigate();
  const [pickerDate, setPickerDate] = useState<{ end: string; start: string }>({
    end: toyyyyMMdd(getCachedDateOfNow()),
    start: toyyyyMMdd(new Date(getCachedDateOfNow().setDate(getCachedDateOfNow().getDate() - 90))),
  });

  const [selected, setSelected] = useState(0);
  const handleTabChange = useCallback((selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  }, []);
  const tabs: TabProps[] = [
    {
      content: t("tabs.overview"),
      id: "overview",
      panelID: "overview",
    },
    {
      content: t("tabs.blockedCodes"),
      id: "blocked-codes",
      panelID: "blocked-codes",
    },
    {
      content: t("tabs.protectedOrders"),
      id: "protected-orders",
      panelID: "protected-orders",
    },
  ];

  const { data: checkoutExtensionData, isError: checkoutExtensionError } = useQuery(
    VerifyCheckoutExtensionInstalledQuery({ shopId: shop?.id }),
  );

  const [extensionsModelOpen, setExtensionsModalOpen] = useState<boolean>(false);

  const {
    data: metricsData,
    isError: metricsError,
    isLoading: metricsLoading,
  } = useQuery(BlockingMetricsQuery({ endDate: pickerDate.end, shopId: shop?.id, startDate: pickerDate.start }));

  const isError = shopError || checkoutExtensionError || metricsError;

  return (
    <Page
      backAction={{
        content: t("backAction"),
        onAction: () => {
          navigate(routeConfig["/"]);
        },
      }}
      compactTitle={false}
      secondaryActions={
        <ButtonGroup>
          <DateRangePicker
            onDateRangeSelect={({ end, start }) => {
              setPickerDate({ end: toyyyyMMdd(end), start: toyyyyMMdd(start) });
            }}
            value={{
              end: new Date(pickerDate.end),
              start: new Date(pickerDate.start),
            }}
          />
          {shop?.extensionBlockingMode === "tracking" && (
            <Button
              icon={ExportIcon}
              onClick={downloadTrackingReport}
              loading={reportFetching}
              disabled={reportFetching || !shop?.id}
            >
              {t("export")}
            </Button>
          )}
          <Button
            onClick={() => {
              setExtensionsModalOpen(true);
            }}
          >
            {t("protectedExtensions")}
          </Button>
          <Button
            accessibilityLabel={t("settings")}
            icon={SettingsIcon}
            onClick={() => {
              navigate(routeConfig.settings["/"]);
            }}
          />
        </ButtonGroup>
      }
      subtitle={t("subtitle")}
      title={t("title")}
      titleMetadata={<Badge>{mapMode(shop?.extensionBlockingMode ?? "...")}</Badge>}
    >
      {isError ? (
        <Banner title={t("error.title")} tone="critical">
          {t("error.description")}
        </Banner>
      ) : (
        <>
          {reportError && (
            <Banner title="Report Generation Error" tone="critical">
              {reportError.message || "Failed to generate tracking report. Please try again."}
            </Banner>
          )}
          <Card padding="0">
            <BlockStack gap="0">
              {!checkoutExtensionData?.installed && (
                <Banner title="Checkout Extension disconnected" tone="warning">
                  Enable the Checkout Extension on your checkout settings page.{" "}
                  <Link target="_blank" url="https://www.keepcart.co/extension-blocking-newcheckout">
                    See Instructions
                  </Link>
                </Banner>
              )}
              {/* @ts-expect-error tinybird is untyped */}
              {checkoutExtensionData?.installed && !metricsData?.data[0]?.blocked_codes > 0 && (
                <Banner title="Checkout Extension connected" tone="info">
                  Your Checkout Extension is successfully installed! Data will appear here soon.
                </Banner>
              )}
              <img src={extensionsBanner} style={{ borderRadius: "0.5rem", width: "100%" }} />
            </BlockStack>
          </Card>
          <Box paddingBlockEnd="400"></Box>

          <div style={{ paddingBlockEnd: "1rem", position: "sticky", top: 0, zIndex: 1 }}>
            <Card padding="0">
              <Tabs onSelect={handleTabChange} selected={selected} tabs={tabs}></Tabs>
            </Card>
          </div>

          <Layout>
            {!shop?.alertEmails ||
              (Array.isArray(shop.alertEmails) && shop.alertEmails.length === 0 && (
                <Layout.Section variant="fullWidth">
                  <Banner title="Alert email address missing" tone="info">
                    <span>Go to </span>
                    <Button
                      onClick={() => {
                        navigate(routeConfig.settings["/"]);
                      }}
                      variant="plain"
                    >
                      settings
                    </Button>
                    <span> to set an email address to get alerted on coupon leaks.</span>
                  </Banner>
                </Layout.Section>
              ))}

            {tabs[selected]?.id === "overview" && (
              <>
                <Layout.Section variant="fullWidth">
                  <InlineGrid columns={{ sm: 3 }} gap="400">
                    {metricsLoading ? (
                      <>
                        <CardSkeleton lines={2} />
                        <CardSkeleton lines={2} />
                        <CardSkeleton lines={2} />
                      </>
                    ) : (
                      <>
                        <MetricCard
                          title="Blocked Codes"
                          tooltip="The number of blocked codes"
                          value={metricsData?.data[0]?.blocked_codes ?? 0}
                        />
                        <MetricCard
                          title="Unique Codes Blocked"
                          tooltip="The number of unique codes blocked"
                          value={metricsData?.data[0]?.unique_blocked_codes ?? 0}
                        />
                        <MetricCard
                          title="Sessions Protected"
                          tooltip="The number of sessions protected"
                          value={metricsData?.data[0]?.sessions_protected ?? 0}
                        />
                      </>
                    )}
                  </InlineGrid>
                </Layout.Section>

                <Layout.Section variant="fullWidth">
                  <InlineGrid columns={{ sm: 3 }} gap="400">
                    {metricsLoading ? (
                      <>
                        <CardSkeleton lines={2} />
                        <CardSkeleton lines={2} />
                        <CardSkeleton lines={2} />
                      </>
                    ) : (
                      <>
                        <RecoveredRevenueWithCommissionMetricCard
                          currency={shop!.currency ?? ""}
                          orderCount={metricsData?.data[0]?.order_count?.toString() ?? ""}
                          recoveredRevenue={metricsData?.data[0]?.recovered_revenue?.toString() ?? ""}
                          recoveredRevenueCommission={
                            metricsData?.data[0]?.recovered_revenue_commission?.toString() ?? ""
                          }
                        />
                        <MetricCard
                          title="Orders Protected Lifetime"
                          tooltip="The total orders protected lifetime"
                          value={metricsData?.data[0]?.orders_protected}
                        />
                        <MetricCard
                          currency={shop!.currency ?? ""}
                          title="Avg. Order ROI Lifetime"
                          tooltip="The amount of revenue recovered per order"
                          value={metricsData?.data[0]?.recovered_per_order}
                        />
                      </>
                    )}
                  </InlineGrid>
                </Layout.Section>

                <Layout.Section variant="fullWidth">
                  <ExtensionAttributionList dateRange={pickerDate} />
                </Layout.Section>
              </>
            )}

            {tabs[selected]!.id === "blocked-codes" && (
              <Layout.Section variant="fullWidth">
                <BlockedCodesTable dateRange={pickerDate} />
              </Layout.Section>
            )}

            {tabs[selected]!.id === "protected-orders" && (
              <Layout.Section variant="fullWidth">
                <ProtectedOrders
                  dateRange={pickerDate}
                  isCalculated={metricsData?.data[0]?.recovered_revenue === null}
                />
              </Layout.Section>
            )}

            <Layout.Section variant="fullWidth">
              <FAQ />
            </Layout.Section>
          </Layout>
          <Modal
            onClose={() => {
              setExtensionsModalOpen(false);
            }}
            open={extensionsModelOpen}
            title="Protected Extensions"
          >
            <Box padding="400">
              <ExtensionsSupported />
            </Box>
          </Modal>
        </>
      )}
    </Page>
  );
};
